import { isAxiosError } from 'axios'
import type { AuthedAxiosRequests } from 'features/auth'
import { z } from 'zod'

import {
  type LotoReportEntry,
  lotoReportEntrySchema,
  type LotoReportScope,
} from './lotoReportSchemas'
import { lotoScopeTags, matchesLotoScope } from './lotoReportScope'
import { loadAllRows } from './lotoRolodexLoad'

const reportRowSchema = z.looseObject({ entry: lotoReportEntrySchema })

function parseScopedReport(
  row: unknown,
  scope: LotoReportScope
): LotoReportEntry | undefined {
  const parsed = reportRowSchema.safeParse(row)
  if (!parsed.success || !matchesLotoScope(parsed.data.entry.tags, scope))
    return undefined
  return parsed.data.entry
}

function requireScopedReport(
  input: LotoReportEntry,
  scope: LotoReportScope
): LotoReportEntry {
  const entry = lotoReportEntrySchema.parse(input)
  if (!matchesLotoScope(entry.tags, scope))
    throw new Error('LOTO report scope mismatch')
  return entry
}

export async function loadLotoReports(
  axios: AuthedAxiosRequests,
  endpoint: string,
  scope: LotoReportScope
): Promise<LotoReportEntry[]> {
  const rows = await loadAllRows(axios, endpoint, {
    type: ['loto-report'],
    tags: lotoScopeTags(scope),
  })
  const reports = new Map<string, LotoReportEntry>()
  for (const row of rows) {
    const entry = parseScopedReport(row, scope)
    if (entry) reports.set(entry.id, entry)
  }
  return [...reports.values()]
}

export async function putLotoReport(
  axios: AuthedAxiosRequests,
  endpoint: string,
  scope: LotoReportScope,
  input: LotoReportEntry
): Promise<void> {
  await axios.put(`${endpoint}/`, [requireScopedReport(input, scope)])
}

export async function deleteLotoReport(
  axios: AuthedAxiosRequests,
  endpoint: string,
  scope: LotoReportScope,
  input: LotoReportEntry
): Promise<void> {
  const entry = requireScopedReport(input, scope)
  try {
    await axios.delete(`${endpoint}/${encodeURIComponent(entry.id)}`)
  } catch (error) {
    if (!isAxiosError(error) || error.response?.status !== 404) throw error
  }
}
